/**
 * Sidebar — Lenavio's primary navigation rail.
 *
 * Visual language ("command center" rail):
 *   • Always-dark gradient background in BOTH light and dark modes —
 *     the brand-blue glow on active items reads consistently against
 *     the deep navy regardless of which theme the user is in.
 *   • Active item: gradient pill + 3px glowing left edge bar +
 *     drop-shadow on icon + small glow dot on the right.
 *   • Hover: subtle white tint + scale 1.01.
 *   • Press: scale 0.96.
 *   • Floating chevron toggle that overlaps the right edge.
 *   • Inline gradient SVG logo (defs use the brand-blue palette).
 *
 * Functional behaviour is unchanged from the prior version: same
 * ``navGroups`` / ``navRoutes`` data, same role / module / feature
 * gating, same mobile drawer transitions, same pinned bottom region
 * for Settings + AI Assistant.
 */
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { navGroups, navRoutes, type NavGroup, type RouteConfig } from "@/lib/routes";
import { useAuth } from "@/hooks/useAuth";
import { usePlanFeatures } from "@/hooks/usePlanFeatures";
import { Badge } from "@/components/ui/Badge";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Shield,
  UserCircle,
} from "lucide-react";
import { ROLE_LABELS } from "@/lib/constants";

// ════════════════════════════════════════════════════════════════════
// Group collapse state — persisted in localStorage so the user's
// chosen folds survive across sessions.
// ════════════════════════════════════════════════════════════════════
const COLLAPSE_STORAGE_KEY = "lenavio.sidebar.collapsedGroups";
// Default state: ORGANIZATION + TIME & PAY expanded, rest collapsed.
const DEFAULT_COLLAPSED: Record<string, boolean> = {
  benefits: true,
  talent: true,
  insights: true,
  compliance: true,
};

function loadCollapsed(): Record<string, boolean> {
  try {
    const raw = window.localStorage.getItem(COLLAPSE_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_COLLAPSED };
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      return parsed as Record<string, boolean>;
    }
  } catch {
    /* localStorage may be disabled — fall through to default */
  }
  return { ...DEFAULT_COLLAPSED };
}

function saveCollapsed(state: Record<string, boolean>): void {
  try {
    window.localStorage.setItem(COLLAPSE_STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  /**
   * On mobile the sidebar lives as an off-canvas drawer instead of a
   * narrow rail: `collapsed=true` hides it entirely (translate-x off
   * screen), `collapsed=false` slides it in on top of the content.
   */
  isMobile?: boolean;
}

interface VisibleRoute {
  route: RouteConfig;
  locked: boolean; // plan-gated and the org doesn't have access
}

export function Sidebar({ collapsed, onToggle, isMobile = false }: SidebarProps) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const { features, hasModule, has } = usePlanFeatures();

  const isTrialing = features?.is_trialing === true;

  const groupedRoutes: Record<NavGroup["id"], VisibleRoute[]> = useMemo(() => {
    const buckets: Record<NavGroup["id"], VisibleRoute[]> = {
      main: [],
      organization: [],
      time_and_pay: [],
      benefits: [],
      talent: [],
      insights: [],
      compliance: [],
      platform: [],
      settings: [],
      ai_assistant: [],
    };
    const isPlatformAdmin = Boolean(user?.is_platform_admin);
    for (const route of navRoutes) {
      if (route.platformAdminOnly && !isPlatformAdmin) continue;
      if (!user || !route.roles.includes(user.role)) {
        if (!route.platformAdminOnly) continue;
      }
      const moduleOk = !route.module || hasModule(route.module);
      if (route.module && features != null && !moduleOk) continue;
      const featureOk = !route.feature || has(route.feature);
      const locked = features != null && !featureOk;
      if (locked && (isTrialing || features?.plan === "free")) continue;
      const groupId: NavGroup["id"] = route.group ?? "main";
      buckets[groupId].push({ route, locked });
    }
    return buckets;
  }, [user, features, has, hasModule, isTrialing]);

  const scrollGroups = navGroups.filter((g) => !g.pinned);
  const pinnedGroups = navGroups.filter((g) => g.pinned);

  // ── Collapse state per group (persisted) ──────────────────────
  // Name avoids collision with the ``collapsed`` prop that controls
  // the whole rail's width.
  const [groupCollapsed, setGroupCollapsed] = useState<Record<string, boolean>>(
    () => loadCollapsed(),
  );
  const toggleGroup = useCallback((groupId: string) => {
    setGroupCollapsed((prev) => {
      const next = { ...prev, [groupId]: !prev[groupId] };
      saveCollapsed(next);
      return next;
    });
  }, []);
  // Auto-expand a group whose item is currently active so the user
  // never sees an active link hidden behind a collapsed header.
  useEffect(() => {
    const activeRoute = navRoutes.find((r) => isPathActive(location.pathname, r.path));
    const groupId = activeRoute?.group;
    if (groupId && groupCollapsed[groupId]) {
      setGroupCollapsed((prev) => {
        const next = { ...prev, [groupId]: false };
        saveCollapsed(next);
        return next;
      });
    }
    // Only re-run on route change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const userInitials = `${user?.first_name?.[0] ?? ""}${user?.last_name?.[0] ?? ""}`.toUpperCase();

  // ── Sidebar scroll-position persistence ────────────────────────
  //
  // Symptom: clicking a nav item that was scrolled into view at the
  // bottom of the rail would "bounce" the rail back to the top after
  // the route transition. The sidebar component itself isn't
  // remounting — but each route navigation re-renders the active
  // <Link> with new aria-current, which on certain browsers triggers
  // an automatic scroll-into-view that drags the nav scrollTop with
  // it.
  //
  // Fix: persist the nav element's scrollTop across renders via a
  // ref + sessionStorage. Restore on mount, save on every scroll,
  // also re-apply after route changes (useLocation re-runs the
  // effect).
  const navRef = useRef<HTMLElement | null>(null);
  const SCROLL_KEY = "lenavio.sidebar.scrollTop";

  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    try {
      const saved = window.sessionStorage.getItem(SCROLL_KEY);
      if (saved != null) {
        const n = parseInt(saved, 10);
        if (!Number.isNaN(n) && n > 0) el.scrollTop = n;
      }
    } catch {
      /* sessionStorage may be disabled — ignore */
    }
  }, [location.pathname]);

  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    let raf = 0;
    const onScroll = () => {
      // rAF-coalesce so we don't write to sessionStorage on every
      // pixel during fast scrolls.
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        try {
          window.sessionStorage.setItem(SCROLL_KEY, String(el.scrollTop));
        } catch {
          /* ignore */
        }
      });
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <aside
      aria-hidden={isMobile && collapsed ? true : undefined}
      style={{
        // Deep-navy "command center" gradient. Same in both light and
        // dark modes — the rail has a consistent identity regardless
        // of which theme the rest of the app is in. Pairs with
        // /logo-white.png for the brand mark below.
        background:
          "linear-gradient(180deg, rgb(var(--sidebar-bg-from)) 0%, rgb(var(--sidebar-bg-to)) 100%)",
      }}
      className={cn(
        "fixed left-0 top-0 h-screen z-40 flex flex-col overflow-hidden",
        "transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]",
        isMobile
          ? cn(
              "w-[264px]",
              collapsed
                ? "-translate-x-full"
                : "translate-x-0 shadow-2xl",
            )
          : collapsed
            ? "w-[76px]"
            : "w-[264px]",
      )}
    >
      {/* Right-edge brand-blue glow strip — gives the rail depth
          without an outright border. */}
      <div
        aria-hidden="true"
        className="absolute top-0 right-0 bottom-0 w-px"
        style={{
          background:
            "linear-gradient(180deg, rgba(27,24,255,0.3) 0%, rgba(27,24,255,0.05) 50%, rgba(105,102,255,0.2) 100%)",
        }}
      />

      {/* Logo row — full-width solid white block so the original
          /logo.png (colourful mark + black wordmark) reads exactly
          as designed, edge-to-edge across the top of the dark
          sidebar. Same rule when collapsed — solid white square
          with the mark only. */}
      <div
        className={cn(
          "flex items-center h-16 shrink-0 bg-white",
          collapsed ? "px-3 justify-center" : "px-5",
        )}
      >
        <img
          src={collapsed ? "/logo-mark.png" : "/logo.png"}
          alt="Lenavio"
          className={cn(
            "w-auto transition-all duration-200",
            collapsed ? "h-7" : "h-6",
          )}
        />
      </div>

      {/* Floating chevron toggle — sits on the right edge, half-overlapping
          the rail. Hidden on the mobile drawer (the backdrop tap closes
          it instead). */}
      {!isMobile && (
        <button
          type="button"
          onClick={onToggle}
          aria-label={collapsed ? t("nav.expandSidebar") : t("nav.collapseSidebar")}
          aria-expanded={!collapsed}
          aria-controls="primary-navigation"
          className={cn(
            "absolute top-7 -right-3.5 z-10 w-7 h-7 rounded-full",
            "flex items-center justify-center cursor-pointer",
            "text-[#818CF8] transition-all duration-200",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#818CF8]",
          )}
          style={{
            background: "#1E1F3B",
            border: "1px solid rgba(27,24,255,0.3)",
            boxShadow:
              "0 2px 12px rgba(0,0,0,0.3), 0 0 8px rgba(27,24,255,0.15)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#2A2B52";
            e.currentTarget.style.boxShadow =
              "0 2px 16px rgba(0,0,0,0.4), 0 0 12px rgba(27,24,255,0.3)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "#1E1F3B";
            e.currentTarget.style.boxShadow =
              "0 2px 12px rgba(0,0,0,0.3), 0 0 8px rgba(27,24,255,0.15)";
          }}
        >
          {collapsed ? (
            <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
          ) : (
            <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" />
          )}
        </button>
      )}

      {/* Scrollable nav region */}
      <nav
        ref={navRef}
        id="primary-navigation"
        aria-label={t("nav.primary")}
        className={cn(
          "flex-1 min-h-0 overflow-y-auto overflow-x-hidden",
          "flex flex-col gap-[3px]",
          collapsed ? "px-2.5 py-1" : "px-3.5 py-1",
        )}
      >
        {scrollGroups.map((group, i) => {
          const items = groupedRoutes[group.id];
          if (!items || items.length === 0) return null;
          return (
            <SectionGroup
              key={group.id}
              group={group}
              items={items}
              railCollapsed={collapsed}
              isFirst={i === 0}
              currentPath={location.pathname}
              onNavigate={isMobile && !collapsed ? onToggle : undefined}
              t={t}
              isGroupCollapsed={!!groupCollapsed[group.id]}
              onToggleGroup={() => toggleGroup(group.id)}
            />
          );
        })}
      </nav>

      {/* Pinned bottom region — Settings + AI Assistant */}
      {pinnedGroups.some((g) => groupedRoutes[g.id].length > 0) && (
        <div
          className={cn(
            "shrink-0 flex flex-col gap-[3px] py-2",
            collapsed ? "px-2.5" : "px-3.5",
          )}
          style={{ borderTop: "1px solid rgba(27,24,255,0.1)" }}
        >
          {pinnedGroups.map((group) => {
            const items = groupedRoutes[group.id];
            if (!items || items.length === 0) return null;
            return (
              <ul key={group.id} className="space-y-0.5 list-none m-0 p-0">
                {items.map(({ route, locked }) => (
                  <SidebarItem
                    key={route.path}
                    route={route}
                    locked={locked}
                    collapsed={collapsed}
                    isActive={isPathActive(location.pathname, route.path)}
                    label={t(route.labelKey, { defaultValue: route.label })}
                    onNavigate={isMobile && !collapsed ? onToggle : undefined}
                  />
                ))}
              </ul>
            );
          })}
        </div>
      )}

      {/* User chip — gradient avatar, name + role + online dot. Action
          icons (profile / security / logout) are stacked compact so
          the full name fits at the standard 264px sidebar width. */}
      <div
        className={cn(
          "shrink-0 flex items-center gap-2.5",
          collapsed ? "px-3 py-3" : "px-3.5 py-3",
        )}
        style={{
          borderTop: "1px solid rgba(27,24,255,0.1)",
          background: "rgba(0,0,0,0.15)",
        }}
      >
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-[11px] font-extrabold tracking-[0.02em] shrink-0"
          style={{
            background: "linear-gradient(135deg, #1B18FF, #4340FF)",
            boxShadow: "0 2px 8px rgba(27,24,255,0.25)",
          }}
          aria-hidden="true"
        >
          {userInitials || "?"}
        </div>
        {!collapsed && (
          <>
            <div className="flex-1 min-w-0">
              <div className="text-[12.5px] font-semibold text-[#E2E8F0] truncate leading-tight">
                {user?.first_name} {user?.last_name}
              </div>
              <div className="text-[9.5px] font-semibold text-[rgba(165,180,252,0.5)] tracking-[0.06em] uppercase truncate leading-tight mt-0.5">
                {user?.role && ROLE_LABELS[user.role]}
              </div>
            </div>
            <div className="flex items-center -mr-1">
              <Link
                to="/my/profile"
                aria-label="My profile"
                title="My profile"
                className="p-1 rounded-md text-[rgba(220,225,255,0.5)] hover:text-white hover:bg-white/5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#818CF8]"
              >
                <UserCircle className="h-3.5 w-3.5" aria-hidden="true" />
              </Link>
              <Link
                to="/account/security"
                aria-label={t("nav.securityMfa")}
                title={t("nav.securityMfa")}
                className="p-1 rounded-md text-[rgba(220,225,255,0.5)] hover:text-white hover:bg-white/5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#818CF8]"
              >
                <Shield className="h-3.5 w-3.5" aria-hidden="true" />
              </Link>
              <button
                type="button"
                onClick={logout}
                aria-label={t("nav.logout")}
                title={t("nav.logout")}
                className="p-1 rounded-md text-[rgba(220,225,255,0.5)] hover:text-[#FCA5A5] hover:bg-white/5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#818CF8]"
              >
                <LogOut className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </div>
          </>
        )}
      </div>
    </aside>
  );
}

// ════════════════════════════════════════════════════════════════════
// Section group — uppercase tracked label + hairline border between
// sections.
// ════════════════════════════════════════════════════════════════════

function SectionGroup({
  group,
  items,
  railCollapsed,
  isFirst,
  currentPath,
  onNavigate,
  t,
  isGroupCollapsed,
  onToggleGroup,
}: {
  group: NavGroup;
  items: VisibleRoute[];
  /** Whether the entire sidebar rail is in its narrow / collapsed
   *  state (the chevron toggle). Distinct from the group's own
   *  collapse state (the per-section accordion). */
  railCollapsed: boolean;
  isFirst: boolean;
  currentPath: string;
  onNavigate?: () => void;
  t: (key: string, opts?: { defaultValue?: string }) => string;
  /** True when this group's accordion is folded shut. */
  isGroupCollapsed: boolean;
  onToggleGroup: () => void;
}) {
  // Hide-from-rail-collapsed: when the WHOLE rail is collapsed,
  // there's no room for a group header — items just stack tightly.
  // The accordion only kicks in on the expanded rail.
  const enableAccordion = !railCollapsed && !!group.label;
  const folded = enableAccordion && isGroupCollapsed;
  return (
    <div
      style={
        !isFirst
          ? {
              borderTop: "1px solid rgba(27,24,255,0.06)",
              marginTop: 2,
              paddingTop: 14,
            }
          : undefined
      }
    >
      {!railCollapsed && group.label && (
        <button
          type="button"
          onClick={onToggleGroup}
          aria-expanded={!folded}
          aria-controls={`nav-group-${group.id}`}
          className={cn(
            "w-full flex items-center justify-between text-[10px] font-bold uppercase whitespace-nowrap",
            "transition-colors duration-150 hover:text-[rgba(165,180,252,0.7)]",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#818CF8] rounded",
          )}
          style={{
            color: "rgba(165,180,252,0.4)",
            letterSpacing: "0.14em",
            padding: "16px 12px 6px",
          }}
        >
          <span>{group.label}</span>
          <ChevronDown
            aria-hidden="true"
            className={cn(
              "h-3.5 w-3.5 transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]",
              folded && "-rotate-90",
            )}
          />
        </button>
      )}
      {/*
        Content-driven height animation via the CSS Grid 1fr/0fr trick.
        The earlier implementation used ``maxHeight: items.length *
        44 + 12`` which under-counted whenever an item wrapped to a
        second line or carried a lock badge — the last entry got
        clipped and overlapped the next group. Grid-rows
        transitioning between ``1fr`` (auto-content) and ``0fr``
        (collapsed) animates the REAL content height without any
        JS measurement, so every item is fully rendered and
        clickable regardless of label length.

        Browser support: Chrome 117+, Firefox 117+, Safari 17.4+ —
        all good as of the target audience. Older browsers degrade
        to an instant show/hide (no animation) but the click target
        is still correct.
      */}
      <div
        id={`nav-group-${group.id}`}
        className={cn(
          "grid transition-[grid-template-rows,opacity]",
          "duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]",
          folded ? "[grid-template-rows:0fr]" : "[grid-template-rows:1fr]",
          folded ? "opacity-0" : "opacity-100",
        )}
        aria-hidden={folded ? true : undefined}
      >
        {/* Inner row that owns the overflow clip; ``min-h-0`` is
            required so the grid track can collapse to zero. The
            ``pb-2`` adds breathing room so the last item never
            butts against the next group's header — fixes the
            "items hard to click" visual bug. */}
        <ul className="min-h-0 overflow-hidden space-y-0.5 list-none m-0 p-0 pb-2">
          {items.map(({ route, locked }) => (
            <SidebarItem
              key={route.path}
              route={route}
              locked={locked}
              collapsed={railCollapsed}
              isActive={isPathActive(currentPath, route.path)}
              label={t(route.labelKey, { defaultValue: route.label })}
              onNavigate={onNavigate}
            />
          ))}
        </ul>
      </div>
    </div>
  );
}

function isPathActive(currentPath: string, routePath: string): boolean {
  if (routePath === "/") return currentPath === "/";
  return currentPath.startsWith(routePath);
}

// ════════════════════════════════════════════════════════════════════
// Single nav item — handles active glow, hover/press scale, locked
// pill, and the right-edge dot indicator.
// ════════════════════════════════════════════════════════════════════

interface SidebarItemProps {
  route: RouteConfig;
  locked: boolean;
  collapsed: boolean;
  isActive: boolean;
  label: string;
  onNavigate?: () => void;
}

function SidebarItem({
  route,
  locked,
  collapsed,
  isActive,
  label,
  onNavigate,
}: SidebarItemProps) {
  const Icon = route.icon;
  const href = locked ? "/settings/billing" : route.path;
  const ariaLabel = collapsed
    ? locked
      ? `${label} (upgrade required)`
      : label
    : undefined;

  return (
    <li>
      <Link
        to={href}
        onClick={onNavigate}
        aria-current={isActive ? "page" : undefined}
        aria-label={ariaLabel}
        title={
          collapsed
            ? locked
              ? `${label} — upgrade to unlock`
              : label
            : locked
              ? "Upgrade to unlock"
              : undefined
        }
        className={cn(
          "group relative flex items-center gap-3 whitespace-nowrap select-none",
          "rounded-[14px] px-4 py-3",
          "transition-all duration-150 ease-[cubic-bezier(0.4,0,0.2,1)]",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#818CF8]",
          collapsed && "justify-center",
          isActive
            ? "text-white font-bold"
            : "text-[rgba(220,225,255,0.7)] font-semibold hover:text-white",
          "hover:scale-[1.01] active:scale-[0.96]",
          locked && "opacity-70",
        )}
        style={{
          fontSize: 14,
          letterSpacing: "0.01em",
          background: isActive
            ? "linear-gradient(135deg, rgba(27,24,255,0.35) 0%, rgba(67,64,255,0.2) 100%)"
            : "rgba(255,255,255,0.02)",
          boxShadow: isActive
            ? "inset 0 1px 0 rgba(255,255,255,0.08), 0 2px 12px rgba(27,24,255,0.15), 0 0 0 1px rgba(27,24,255,0.2)"
            : "inset 0 1px 0 rgba(255,255,255,0.02), 0 0 0 1px rgba(255,255,255,0.03)",
        }}
      >
        {/* Active glow bar — left edge */}
        {isActive && (
          <span
            aria-hidden="true"
            className="absolute left-0 w-[3px] rounded-r"
            style={{
              top: "22%",
              bottom: "22%",
              background: "linear-gradient(180deg, #6966FF, #1B18FF)",
              boxShadow:
                "0 0 10px rgba(27,24,255,0.7), 0 0 4px rgba(105,102,255,0.5)",
            }}
          />
        )}

        {/* Icon — gets a glow drop-shadow when active */}
        <span
          className="flex items-center justify-center w-6 h-6 shrink-0"
          style={{
            filter: isActive
              ? "drop-shadow(0 0 5px rgba(105,102,255,0.5))"
              : undefined,
            transition: "filter 0.15s",
          }}
        >
          <Icon className="h-[19px] w-[19px]" aria-hidden="true" />
        </span>

        {!collapsed && (
          <>
            <span className="flex-1 truncate">{label}</span>
            {locked && (
              <Badge
                variant="default"
                className="text-[10px] uppercase tracking-wide bg-white/10 text-white border-white/10"
              >
                Pro
              </Badge>
            )}
            {isActive && !locked && (
              <span
                aria-hidden="true"
                className="ml-auto w-[7px] h-[7px] rounded-full"
                style={{
                  background: "#A5B4FC",
                  boxShadow: "0 0 8px rgba(165,180,252,0.7)",
                }}
              />
            )}
          </>
        )}
      </Link>
    </li>
  );
}
